import React from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiArrowLeft, FiAward } = FiIcons;

const Certificate = ({ user }) => {
  const navigate = useNavigate();
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const handlePrint = () => {
    window.print();
  };

  if (!user || !user.completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
            <SafeIcon icon={FiAward} className="text-4xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Certificate Locked</h2>
          <p className="text-gray-600 mb-6">Complete all modules to unlock your certificate!</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 flex flex-col items-center">
      {/* Action Bar */}
      <div className="w-full max-w-5xl mb-6 flex justify-between items-center no-print">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium px-4 py-2 rounded-lg hover:bg-white transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          Back to Dashboard
        </button>
        <button 
          onClick={handlePrint}
          className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg font-bold shadow-md hover:bg-green-700 transition-colors"
        >
          <SafeIcon icon={FiDownload} />
          Download / Print
        </button>
      </div>

      {/* Certificate Container */}
      <div className="certificate-container bg-white w-full max-w-5xl aspect-[1.414/1] relative shadow-2xl overflow-hidden text-center p-12 md:p-16 flex flex-col items-center justify-between border-[20px] border-double border-gray-100">
        
        {/* Decorative Border Layer */}
        <div className="absolute inset-4 border-4 border-green-700 rounded-lg pointer-events-none"></div>
        <div className="absolute inset-6 border border-yellow-500 rounded-lg pointer-events-none"></div>
        
        {/* Corner Decorations */}
        <div className="absolute top-8 left-8 w-16 h-16 border-t-4 border-l-4 border-yellow-500 rounded-tl-3xl"></div>
        <div className="absolute top-8 right-8 w-16 h-16 border-t-4 border-r-4 border-yellow-500 rounded-tr-3xl"></div>
        <div className="absolute bottom-8 left-8 w-16 h-16 border-b-4 border-l-4 border-yellow-500 rounded-bl-3xl"></div>
        <div className="absolute bottom-8 right-8 w-16 h-16 border-b-4 border-r-4 border-yellow-500 rounded-br-3xl"></div>

        {/* Background Watermark */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
          <span className="font-amharic text-[400px]">ግ</span>
        </div>

        {/* Header */}
        <div className="relative z-10 pt-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center text-white shadow-xl mb-6 border-4 border-yellow-400">
            <span className="font-amharic text-5xl font-bold">ግ</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 tracking-wide mb-2">Certificate</h1>
          <h2 className="text-2xl md:text-3xl font-serif text-green-700 tracking-widest uppercase">of Completion</h2>
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-3xl mx-auto space-y-8">
          <p className="text-gray-500 text-lg uppercase tracking-widest">This certifies that</p>
          
          <div className="border-b-2 border-gray-300 pb-2">
            <h3 className="text-4xl md:text-6xl font-serif font-bold text-gray-800 italic">{user.name}</h3>
          </div>
          
          <p className="text-gray-500 text-lg uppercase tracking-widest">has successfully mastered</p>
          
          <div className="py-4">
            <h4 className="text-3xl md:text-4xl font-bold text-green-800 mb-2">The Social Grammar of Amharic</h4>
            <p className="text-gray-600">Nouns, Pronouns, and Cultural Dynamics</p>
          </div>
        </div>

        {/* Footer / Signatures */}
        <div className="relative z-10 w-full flex justify-between items-end pt-12 px-12">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-800 border-b border-gray-400 pb-1 px-8 mb-2">{date}</div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Date Completed</p>
          </div>

          <div className="mb-4">
             <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                <SafeIcon icon={FiAward} className="text-4xl text-yellow-800" />
             </div>
          </div>

          <div className="text-center">
            <div className="text-lg font-bold text-gray-800 font-serif italic border-b border-gray-400 pb-1 px-8 mb-2">BK Amharic Team</div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Course Instructor</p>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-green-600 via-yellow-400 to-red-600"></div>
      </div>
    </div>
  );
};

export default Certificate;